import { SLOT_TO_ROLE, ASSIGN_ALL_ORDER_TYPES, WILDCARD_ORDER_TYPE } from './constants';
import { SPECIAL_REQUIREMENTS, COMMODITY_SPECIAL_REQUIREMENTS } from './profileConstants';
import { uniq, zipObject } from 'lodash';
import { SUBREGION_IMPORT_ATTRIBUTES } from '../constants';

// The UI sentinel must never reach the database. Shared so assignment rows and the profile's
// default cannot disagree about what "all" means.
export const collapseOrderTypes = (orderTypes) => {
    if (!orderTypes?.length) return [];
    if (orderTypes.includes(ASSIGN_ALL_ORDER_TYPES)) return [WILDCARD_ORDER_TYPE];
    return uniq(orderTypes);
};

// The inverse of collapseOrderTypes: the dropdown has no option for the persisted 'all', so map it
// back to the sentinel.
export const toFormOrderTypes = (persisted) =>
    (persisted || []).includes(WILDCARD_ORDER_TYPE) ? [ASSIGN_ALL_ORDER_TYPES] : persisted || [];

// What the apply bar starts on: the profile's default (the profile holds the default, apply
// materializes it). A profile whose default is '{}' starts on "All" rather than empty, which would
// leave Attach disabled.
export const applyOrderTypesFor = (deliveryProfile) => {
    const seeded = toFormOrderTypes(deliveryProfile?.default_order_types);
    return seeded.length ? seeded : [ASSIGN_ALL_ORDER_TYPES];
};

// "All order types" is mutually exclusive with the specific values. Shared by the apply bar and
// the profile modal: both drive the same ORDER_TYPE_OPTIONS, and without this the modal lets the
// user build a mixed state that collapseOrderTypes silently discards on save.
export const exclusiveOrderTypes = (previous = [], next = []) => {
    const hadAll = previous.includes(ASSIGN_ALL_ORDER_TYPES);
    const hasAll = next.includes(ASSIGN_ALL_ORDER_TYPES);

    // Just ticked "All" -- it replaces everything.
    if (hasAll && !hadAll) return [ASSIGN_ALL_ORDER_TYPES];
    // "All" was already on and something specific was added -- the specific choice wins.
    if (hasAll && next.length > 1) return next.filter((value) => value !== ASSIGN_ALL_ORDER_TYPES);
    return next;
};

// zip_type is inherited from the profile, not chosen per shipper.
//
// No `zipType` fallback on purpose. This is the layer that actually writes zip_type, so a
// `|| 'DROPOFF'` here would quietly route every order off the dropoff zip whenever a caller
// forgot to pass the profile's value. Returning [] instead makes the omission visible: nothing
// is applied.
export const buildAssignmentRows = ({ deliveryProfile, shipperIds, zipType, orderTypes }) => {
    const ids = uniq((shipperIds || []).filter(Boolean));

    if (!zipType || !ids.length || !orderTypes?.length) return [];

    const types = collapseOrderTypes(orderTypes);

    return ids.flatMap((shipper_id) =>
        types.map((order_type) => ({
            delivery_profile_id: deliveryProfile?.delivery_profile_id,
            shipper_id,
            zip_type: zipType,
            order_type,
        }))
    );
};

const assignmentZipTypes = (deliveryProfile) =>
    uniq((deliveryProfile?.auto_assignments || []).map((a) => a.zip_type).filter(Boolean));

// The zip type the profile's shippers actually route on. autoAssign reads each assignment's own
// zip_type, so when they all agree that wins over delivery_profiles.assign_zip_type, which can
// hold a stale 'DROPOFF' default. Mixed or no assignments fall back to the profile's column.
export const routedZipType = (deliveryProfile) => {
    const zipTypes = assignmentZipTypes(deliveryProfile);
    return zipTypes.length === 1 ? zipTypes[0] : deliveryProfile?.assign_zip_type;
};

// Shippers on the profile route on different zip types, so there's no single value to show.
export const hasMixedZipTypes = (deliveryProfile) => assignmentZipTypes(deliveryProfile).length > 1;

// Filters carriers eligible for a mapping slot by their client role. A carrier
// may serve multiple slots when it holds multiple roles (e.g. CARRIER +
// MIDDLE_MILE_CARRIER).
export const filterCarriersByLeg = (slot, carriers) => {
    const requiredRole = SLOT_TO_ROLE[slot];
    if (!requiredRole) return [];
    return (carriers || []).filter((carrier) => !!carrier?.user?.roles?.[requiredRole]);
};

const UUID_RE = /^[0-9a-f]{8}-([0-9a-f]{4}-){3}[0-9a-f]{12}$/;

const hashMapping = (mapping) =>
    btoa(
        ['carrier_id', 'middle_mile_carrier_id', 'warehouse_carrier_id']
            .map((attr) => mapping[attr])
            .filter((id) => id)
            .join('|')
    );

export const parseImportedSubregions = (headers, body, ctx, existingSubregions) => {
    const ATTRS = SUBREGION_IMPORT_ATTRIBUTES;
    const { deliveryProfileId, carriersById, mmCarriersById, warehouseCarriersById } = ctx;

    const isValidMapping = (mapping) =>
        !!mapping.carrier_id &&
        !!carriersById[mapping.carrier_id] &&
        (!mapping.middle_mile_carrier_id || !!mmCarriersById[mapping.middle_mile_carrier_id]) &&
        (!mapping.warehouse_carrier_id || !!warehouseCarriersById[mapping.warehouse_carrier_id]);

    const yes = (cell) => !!cell && !['no', 'false'].includes(cell.toString().toLowerCase());
    const hasPrimaryColumn = headers.includes(ATTRS.is_primary);

    const importedByName = body.reduce((acc, row, rowIndex) => {
        const parsed = zipObject(headers, row);
        if (Object.values(parsed).every((cell) => !cell)) return acc;

        Object.entries(parsed).forEach(([key, value]) => {
            switch (key) {
                case ATTRS.zip:
                    if (!value) throw new Error(`Row ${rowIndex + 1} is missing a zip code`);
                    if (value.length !== 5) throw new Error(`Row ${rowIndex + 1} has an invalid zip code`);
                    break;
                case ATTRS.carrier_id:
                case ATTRS.middle_mile_carrier_id:
                case ATTRS.warehouse_id:
                case ATTRS.warehouse_carrier_id:
                    if (value && !UUID_RE.test(value)) throw new Error(`Row ${rowIndex + 1} has an invalid Id`);
                    break;
            }
        });

        const subregionName = parsed[ATTRS.name];
        const prev = acc[subregionName]?.carriers || [];
        const mapping = {
            is_direct: yes(parsed[ATTRS.is_direct]),
            carrier_id: parsed[ATTRS.carrier_id] || null,
            warehouse_id: parsed[ATTRS.warehouse_id] || null,
            middle_mile_carrier_id: parsed[ATTRS.middle_mile_carrier_id] || null,
            warehouse_carrier_id: parsed[ATTRS.warehouse_carrier_id] || null,
            site_code: parsed[ATTRS.site_code] || null,
            site_contact_name: parsed[ATTRS.site_contact_name] || null,
            site_contact_email: parsed[ATTRS.site_contact_email] || null,
            site_contact_phone: parsed[ATTRS.site_contact_phone] || null,
        };
        const next = [
            ...prev,
            ...(isValidMapping(mapping) && !prev.find((m) => hashMapping(m) === hashMapping(mapping)) ? [mapping] : []),
        ];
        // Keyed like the dedupe: a carrier is primary when any of its lines says so.
        const primaryHashes =
            yes(parsed[ATTRS.is_primary]) && isValidMapping(mapping)
                ? uniq([...(acc[subregionName]?.primaryHashes || []), hashMapping(mapping)])
                : acc[subregionName]?.primaryHashes || [];
        if (primaryHashes.length > 1) {
            throw new Error(`Subregion ${subregionName} has more than one primary carrier`);
        }

        return {
            ...acc,
            [subregionName]: {
                zips: uniq([...(acc[subregionName]?.zips || []), parsed[ATTRS.zip]]),
                carriers: next,
                primaryHashes,
            },
        };
    }, {});

    const to_remove = existingSubregions.filter((s) => !!importedByName[s.name]).map((s) => s.subregion_id);

    // With a Primary column, the file says which carrier is primary, and a subregion may have none.
    // Without one, a subregion's first carrier in the file is its primary.
    const isPrimary = (carrier, idx, primaryHashes) =>
        hasPrimaryColumn ? primaryHashes.includes(hashMapping(carrier)) : idx === 0;

    const to_add = Object.entries(importedByName).map(([name, { zips, carriers, primaryHashes }]) => ({
        name,
        delivery_profile_id: deliveryProfileId,
        zips: { data: zips.map((zip) => ({ zip })) },
        carriers: {
            data: carriers.map((carrier, idx) => ({
                ...carrier,
                is_primary: isPrimary(carrier, idx, primaryHashes) ? true : null,
            })),
        },
    }));

    return { to_add, to_remove };
};

export const unitToMeters = (value, unit) => {
    const v = Number(value) || 0;
    return unit === 'km' ? v * 1000 : v * 1609.344;
};

// Equirectangular bounding box that contains the circle of given radius. Use
// to drive fitBounds so the viewport (and therefore loaded ZCTA tiles) covers
// the entire radius before querying.
export const getCircleBounds = (center, radiusMeters) => {
    const [lng, lat] = center;
    const earthRadius = 6371000;
    const latOffset = (radiusMeters / earthRadius) * (180 / Math.PI);
    const lngOffset = latOffset / Math.cos((lat * Math.PI) / 180);
    return [
        [lng - lngOffset, lat - latOffset],
        [lng + lngOffset, lat + latOffset],
    ];
};

const haversineMeters = (a, b) => {
    const R = 6371000;
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(b[1] - a[1]);
    const dLng = toRad(b[0] - a[0]);
    const lat1 = toRad(a[1]);
    const lat2 = toRad(b[1]);
    const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(x));
};

const flattenPolygonCoords = (geometry) => {
    if (!geometry) return [];
    if (geometry.type === 'Polygon') return geometry.coordinates.flat();
    if (geometry.type === 'MultiPolygon') return geometry.coordinates.flat(2);
    return [];
};

const averageCoord = (coords) => {
    if (!coords.length) return null;
    let sumLng = 0;
    let sumLat = 0;
    for (const [lng, lat] of coords) {
        sumLng += lng;
        sumLat += lat;
    }
    return [sumLng / coords.length, sumLat / coords.length];
};

// Returns the unique zips (NAME20) whose averaged centroid sits in the band
// between minMeters and maxMeters of the given center (inclusive on max,
// inclusive on min). Aggregates coordinates across all feature shards so a
// ZCTA split across tile boundaries gets a single, correct centroid.
export const findZipsInRadius = (map, center, minMeters, maxMeters) => {
    if (!map || !center || !maxMeters || maxMeters <= minMeters) return [];
    try {
        const features = map.queryRenderedFeatures({ layers: ['outlines-layer'] });
        const coordsByZip = new Map();
        for (const feature of features) {
            const zip = feature.properties?.NAME20;
            if (!zip) continue;
            const coords = flattenPolygonCoords(feature.geometry);
            if (!coords.length) continue;
            const existing = coordsByZip.get(zip);
            if (existing) {
                existing.push(...coords);
            } else {
                coordsByZip.set(zip, coords);
            }
        }
        const within = [];
        for (const [zip, coords] of coordsByZip) {
            const centroid = averageCoord(coords);
            if (!centroid) continue;
            const dist = haversineMeters(center, centroid);
            if (dist >= minMeters && dist <= maxMeters) {
                within.push(zip);
            }
        }
        return within.sort();
    } catch (_) {
        return [];
    }
};

// The subregion's primary carrier, the one auto-assign uses. Null when none is set.
export const primaryMapping = (carriers) => (carriers || []).find((m) => m.is_primary) || null;

// For exports: the primary, else the first carrier, so a subregion with no primary still exports
// its zips.
export const exportMapping = (carriers) => primaryMapping(carriers) || carriers?.[0] || null;

// delivery_profile_carrier_statuses enum values, worst first, with their badge label and StatusBadge tone.
const TARIFF_STATUSES = {
    NOT_SETUP: { label: 'Not Setup', tone: 'danger' },
    REVIEW: { label: 'Review', tone: 'warning' },
    SETUP: { label: 'Setup', tone: 'success' },
};

const INTEGRATION_STATUSES = {
    NOT_STARTED: { label: 'Not Started', tone: 'neutral' },
    FAILING: { label: 'Failing', tone: 'danger' },
    NEEDS_INFO: { label: 'Needs Info', tone: 'warning' },
    REQUEST_SENT: { label: 'Request sent', tone: 'warning' },
    OPENED: { label: 'Opened', tone: 'warning' },
    DETAILS_RECEIVED: { label: 'Details Received', tone: 'warning' },
    TESTING: { label: 'Testing', tone: 'warning' },
    ENGINEER_REVIEW: { label: 'Engineer Review', tone: 'warning' },
    LIVE: { label: 'Live', tone: 'success' },
};

// The view has no row for a carrier with no pricing or integration for the profile's shippers.
const NO_STATUS_ROW = { tariff: 'NOT_SETUP', integration: 'NOT_STARTED' };

// QUERY_CARRIER_STATUSES rows to `{ [carrier_id]: { tariff, integration } }` for every requested carrier.
export const carrierStatusesById = (carrierIds = [], results = []) => {
    const rows = Object.fromEntries(
        (results || []).map((r) => [r.carrier_id, { tariff: r.tariff_status, integration: r.integration_status }])
    );
    return Object.fromEntries(carrierIds.map((id) => [id, rows[id] || NO_STATUS_ROW]));
};

// `{ label, tone }` for a badge, or null while the statuses are loading so a badge doesn't flash a value.
export const statusBadge = (statuses, kind) => {
    const value = statuses?.[kind];
    if (!value) return null;
    return (kind === 'tariff' ? TARIFF_STATUSES : INTEGRATION_STATUSES)[value] || null;
};

// Every requested carrier's region zips, de-duplicated and sorted; [] for a carrier with none.
export const carrierRegionZips = (carrierIds = [], regions = []) => {
    const byCarrier = Object.fromEntries(carrierIds.map((id) => [id, new Set()]));
    for (const region of regions) {
        const zips = byCarrier[region.client_id];
        if (!zips) continue;
        (region.zips || []).forEach(({ zip }) => zip && zips.add(zip));
    }
    return Object.fromEntries(Object.entries(byCarrier).map(([id, zips]) => [id, [...zips].sort()]));
};

// Whether a profile belongs to the scope the user works in: an Onward admin's profiles have no
// organization, a corporate admin's belong to their organization.
export const profileInScope = (profile, scope) => {
    if (!profile || !scope) return false;
    if (scope.kind === 'onward') return !profile.organization_id;
    if (scope.kind === 'corp') return !!scope.org_id && profile.organization_id === scope.org_id;
    return false;
};

// For SubregionListItem's `readiness` option: `tariff` and `integrated` are the primary carrier's badge
// tones, null with no primary or while its statuses load.
export const subregionReadiness = (subregion, carriersById = {}) => {
    const primary = primaryMapping(subregion?.carriers);
    const statuses = primary ? carriersById[primary.carrier_id]?.statuses : null;
    return {
        primary: !!primary,
        tariff: statusBadge(statuses, 'tariff')?.tone || null,
        integrated: statusBadge(statuses, 'integration')?.tone || null,
    };
};

export const serializeSubregionsForExport = (subregions, { genName }) =>
    (subregions || []).reduce((acc, subregion, idx) => {
        const name = genName(subregion, idx);
        const mapping = exportMapping(subregion.carriers);
        if (!mapping) return acc;
        return [
            ...acc,
            ...subregion.zips.map(({ zip }) => ({
                name,
                zip,
                is_primary: !!mapping.is_primary,
                is_direct: mapping.is_direct,
                carrier_id: mapping.carrier_id,
                warehouse_id: mapping.warehouse_id,
                middle_mile_carrier_id: mapping.middle_mile_carrier_id,
            })),
        ];
    }, []);

// --- Delivery profile form ------------------------------------------------------------

// The union across the selected commodities, de-duplicated and in SPECIAL_REQUIREMENTS order.
export const requirementsForCommodities = (commodities) => {
    const codes = new Set((commodities || []).flatMap((commodity) => COMMODITY_SPECIAL_REQUIREMENTS[commodity] || []));
    return SPECIAL_REQUIREMENTS.filter(({ value }) => codes.has(value));
};

// Keeps only the selections the commodities still offer. Used on hydration (the requirements map is
// provisional, so a stored value can fall out of it and would then render nowhere yet still be
// re-persisted on save) and on every commodity change, so a requirement shared by the remaining
// commodities survives while one that no longer applies is dropped.
export const validRequirementsForCommodities = (selected, commodities) => {
    const available = requirementsForCommodities(commodities).map(({ value }) => value);
    return (selected || []).filter((value) => available.includes(value));
};

// Empty object means valid. Special requirements are absent because they are optional.
export const validateProfileForm = (values = {}) => {
    const errors = {};

    if (!values.name?.trim()) errors.name = 'Name is required';
    if (!values.assign_zip_type) errors.assign_zip_type = 'Assign by zip is required';
    if (!values.commodity?.length) errors.commodity = 'Select at least one commodity';
    if (!values.delivery_types?.length) errors.delivery_types = 'Select at least one service type';
    if (!values.default_order_types?.length) errors.default_order_types = 'Select at least one order type';

    return errors;
};

export const labelsFor = (options, values) =>
    (values || []).map((value) => options.find((o) => o.value === value)?.label).filter(Boolean);

// TODO(ENG-9324 follow-up): filter by coverage of the subregion's zips and by the profile's delivery
// types once carriers have that data.
export const pickableCarriers = (options = [], mappings = [], search = '') => {
    const mapped = new Set(mappings.map((m) => m.carrier_id).filter(Boolean));
    const query = search.trim().toLowerCase();
    return options.filter(
        (c) => !mapped.has(c.client_id) && (!query || (c.business_name || '').toLowerCase().includes(query))
    );
};
